import { parseSafeReturn, SAFE_RETURN_STORAGE_KEY, storeSafeReturn } from './safeReturn';
import {
  canonicalProductPath,
  type ProductDeploymentProfile,
} from '../next/app/navigation/productPath';

export const ACCESS_PENDING_PATH = '/access-pending';
export const ACCESS_PENDING_NEXT_KEY = SAFE_RETURN_STORAGE_KEY;

export type AccessPendingNextSource = 'login-query' | 'current-page';

type AccessPendingRedirectOptions = {
  nextSource: AccessPendingNextSource;
  candidate?: string | null;
};

export function isAccessPendingError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
  const detail = response?.data?.detail;
  return response?.status === 403
    && !!detail
    && typeof detail === 'object'
    && (detail as { code?: unknown }).code === 'access_pending';
}

function readNextCandidate(nextSource: AccessPendingNextSource): string | null {
  if (nextSource === 'login-query') {
    return new URLSearchParams(window.location.search).get('next');
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash || ''}`;
}

export function stashAccessPendingNext({ nextSource, candidate: explicitCandidate }: AccessPendingRedirectOptions): string | null {
  const candidate = explicitCandidate !== undefined ? explicitCandidate : readNextCandidate(nextSource);
  try {
    if (!candidate) {
      sessionStorage.removeItem(ACCESS_PENDING_NEXT_KEY);
      return null;
    }
    return storeSafeReturn(candidate);
  } catch (e) {
    console.warn('Failed to persist the Product safe return', e);
    return null;
  }
}

export function routeToAccessPending(
  options: AccessPendingRedirectOptions,
  profile: ProductDeploymentProfile = __PINPOINT_DEPLOYMENT_PROFILE__,
): void {
  const accessPendingPath = canonicalProductPath(ACCESS_PENDING_PATH, profile);
  const candidate = options.candidate !== undefined
    ? options.candidate
    : readNextCandidate(options.nextSource);
  const safeCandidate = parseSafeReturn(candidate);
  if (window.location.pathname === accessPendingPath) {
    // A hard reload verifies the stored token while already on the redeem page.
    // Never replace its previously reviewed deep return with /access-pending.
    if (safeCandidate && safeCandidate !== accessPendingPath) storeSafeReturn(safeCandidate);
    return;
  }

  // Control-page boot verifiers can race the auth request that already stored a
  // reviewed deep return. Invalid /login or /access-pending candidates never
  // erase that record; ordinary no-return login explicitly supplies /practice.
  if (safeCandidate) storeSafeReturn(safeCandidate);
  window.location.assign(accessPendingPath);
}
